/* eslint-disable jsx-a11y/alt-text */
import { cloneElement, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Loading from "../../components/loading/loading";
import clients from "../../icons/clients.svg";
import dashboard from "../../icons/dashboard.svg";
import { userRequest } from "../../requestMethods";
import {
  buildCohortRetention,
  buildCohortTable,
  buildMonthlySeries,
  buildSummary,
  defaultRange,
  monthLabel,
  monthRange,
  printFileName,
} from "../../util/companyAcquisition";
import "./companyAcquisition.css";
import "./report.css";

// Paleta dos gráficos. Par validado nos seis checks de contraste/CVD contra
// fundo branco (banda de luminosidade, piso de croma, separação protan/deutan/
// tritan, piso de visão normal e contraste >= 3:1). Não trocar por cor "da
// marca" sem revalidar: o laranja claro de antes ficava em 2,6:1.
const COR_ENTRADA = "#D9541E";
const COR_SAIDA = "#2a78d6";
// Cinza um passo fora da superfície: grade e eixos recuados.
const COR_GRADE = "#e8e8e8";
const COR_EIXO = "#999999";

const eixoTick = { fontSize: 12, fill: COR_EIXO };

// Animação desligada: o "Gerar PDF" imprime o DOM como está, e uma barra no meio
// da animação sairia cortada no papel.

// Tamanho fixo do gráfico no papel. A4 retrato com margem de 10mm dá ~190mm de
// área útil (~718px a 96dpi); 680 deixa folga.
const LARGURA_IMPRESSAO = 680;
const ALTURA_IMPRESSAO = 260;
const ALTURA_TELA = 300;

/**
 * Na tela o gráfico é responsivo; no papel ele recebe largura fixa.
 *
 * O ResponsiveContainer mede o container por ResizeObserver, que dispara de
 * forma assíncrona. Como a folha de impressão muda a largura disponível (some a
 * sidebar, os gráficos passam a uma coluna), a remedição caía no meio do
 * snapshot da impressão e o recharts imprimia barras com geometria de dois
 * layouts ao mesmo tempo. Com width/height fixos não há o que medir.
 */
const ChartFrame = ({ printMode, children }) =>
  printMode ? (
    cloneElement(children, {
      width: LARGURA_IMPRESSAO,
      height: ALTURA_IMPRESSAO,
    })
  ) : (
    <ResponsiveContainer width="100%" height={ALTURA_TELA}>
      {children}
    </ResponsiveContainer>
  );

const Card = ({ label, value, hint, valueClass }) => (
  <div>
    <div className="acq_card_label">{label}</div>
    <div className={`acq_card_value ${valueClass || ""}`}>{value}</div>
    {hint ? <div className="acq_card_hint">{hint}</div> : null}
  </div>
);

const BlockHead = ({ icon, title, subtitle }) => (
  <div className="acq_block_head">
    <img src={icon} />
    <div>
      <div className="labelTitle">{title}</div>
      <div className="labelMsgData">{subtitle}</div>
    </div>
  </div>
);

const num = (value) => (value === null || value === undefined ? "—" : value);
const days = (value) =>
  value === null || value === undefined ? "—" : `${value} d`;
const percent = (value) =>
  value === null || value === undefined ? "—" : `${value}%`;
const signed = (value) => {
  if (value === null || value === undefined) return "—";
  return `${value > 0 ? "+" : ""}${value}`;
};

export default function CompanyAcquisition() {
  const initial = defaultRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = useSelector((state) => state.user.currentUser);
  const nav = useNavigate();

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 0) {
      getAcquisition();
      window.scrollTo(0, 0);
    } else {
      nav(`../login`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAcquisition = async () => {
    try {
      setIsLoading(true);
      const res = await userRequest.get(`company/acquisition`);
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setCompanies([]);
    }
    setIsLoading(false);
  };

  // O navegador usa o document.title como nome default do PDF; guardamos o
  // título original para devolver depois que o diálogo fecha.
  const originalTitle = useRef(null);
  const [printMode, setPrintMode] = useState(false);

  useEffect(() => {
    // A classe entra também quando a impressão parte do Ctrl/Cmd+P, não só do
    // botão — as regras @media print do dashboard são escopadas nela.
    //
    // flushSync força o React a repintar AINDA DENTRO do beforeprint: o
    // navegador só tira o snapshot depois que o handler retorna, então o
    // gráfico já está com o tamanho de papel quando a página é capturada. Com
    // setState normal a atualização ficaria para depois da impressão.
    const onBefore = () => {
      document.body.classList.add("acq_print");
      flushSync(() => setPrintMode(true));
    };
    const onAfter = () => {
      document.body.classList.remove("acq_print");
      setPrintMode(false);
      if (originalTitle.current !== null) {
        document.title = originalTitle.current;
        originalTitle.current = null;
      }
    };

    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);
    return () => {
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
      onAfter();
    };
  }, []);

  const gerarPdf = () => {
    if (originalTitle.current === null) originalTitle.current = document.title;
    document.title = printFileName(from, to);
    window.print();
  };

  const rangeIsValid = monthRange(from, to).length > 0;

  const summary = useMemo(
    () => (rangeIsValid ? buildSummary(companies, from, to) : null),
    [companies, from, to, rangeIsValid],
  );
  const retencao = useMemo(
    () => (rangeIsValid ? buildCohortRetention(companies, from, to) : null),
    [companies, from, to, rangeIsValid],
  );
  const series = useMemo(
    () => (rangeIsValid ? buildMonthlySeries(companies, from, to) : []),
    [companies, from, to, rangeIsValid],
  );
  const cohorts = useMemo(
    () => (rangeIsValid ? buildCohortTable(companies, from, to) : []),
    [companies, from, to, rangeIsValid],
  );

  const deltaClass = (delta) => {
    if (delta === null || delta === undefined) return "acq_delta_flat";
    if (delta > 0) return "acq_delta_up";
    if (delta < 0) return "acq_delta_down";
    return "acq_delta_flat";
  };

  const deltaText = (delta) => {
    if (delta === null || delta === undefined) return "—";
    return `${delta > 0 ? "+" : ""}${delta}%`;
  };

  const periodLabel = rangeIsValid
    ? `${monthLabel(from)} a ${monthLabel(to)}`
    : "período inválido";

  const cohortTotals = cohorts.reduce(
    (acc, row) => ({
      entradas: acc.entradas + row.entradas,
      ativasHoje: acc.ativasHoje + row.ativasHoje,
      desativadas: acc.desativadas + row.desativadas,
    }),
    { entradas: 0, ativasHoje: 0, desativadas: 0 },
  );

  const resetRange = () => {
    const range = defaultRange();
    setFrom(range.from);
    setTo(range.to);
  };

  return (
    <div className="acq_container">
      <div className="acq_header">
        <div>
          <div className="labelTitle">Aquisição de empresas</div>
          <div className="labelMsgData">
            Entrada, saída e evolução da base de clientes — {periodLabel}
          </div>
        </div>
        <div className="acq_filter">
          <div className="acq_filter_item">
            <label htmlFor="acq_from">De</label>
            <input
              id="acq_from"
              type="month"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="acq_filter_item">
            <label htmlFor="acq_to">Até</label>
            <input
              id="acq_to"
              type="month"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <button className="acq_btn acq_btn_clear" onClick={resetRange}>
            Ano corrente
          </button>
          <button className="acq_btn" onClick={getAcquisition}>
            Atualizar
          </button>
          <button
            className="acq_btn"
            onClick={gerarPdf}
            disabled={isLoading || !rangeIsValid}
          >
            Gerar PDF
          </button>
        </div>
      </div>

      <div className="acq_print_only acq_print_header">
        <div className="labelTitle">Aquisição de empresas</div>
        <div className="labelMsgData">
          Período: {periodLabel} · Base de {companies.length} empresa(s)
          cadastrada(s)
        </div>
      </div>

      {!rangeIsValid ? (
        <div className="acq_warning">
          O mês inicial precisa ser igual ou anterior ao mês final.
        </div>
      ) : null}

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Bloco 1 — aquisição */}
          <div className="acq_block">
            <div className="div_home_custom">
              <BlockHead
                icon={clients}
                title="Aquisição no período"
                subtitle={`Empresas cadastradas entre ${monthLabel(
                  from,
                )} e ${monthLabel(to)}`}
              />
              <div className="acq_cards">
                <Card
                  label="Novas empresas"
                  value={num(summary?.novas)}
                  hint={`em ${summary?.months || 0} mês(es)`}
                />
                <Card
                  label="Período anterior"
                  value={num(summary?.novasAnterior)}
                  hint={
                    summary?.periodoAnterior
                      ? `${monthLabel(
                          summary.periodoAnterior.from,
                        )} a ${monthLabel(summary.periodoAnterior.to)}`
                      : "—"
                  }
                />
                <Card
                  label="Variação"
                  value={deltaText(summary?.deltaPct)}
                  valueClass={deltaClass(summary?.deltaPct)}
                  hint="vs. período anterior"
                />
                <Card
                  label="Média por mês"
                  value={num(summary?.mediaMes)}
                  hint="novas empresas/mês"
                />
                <Card
                  label="Base ativa"
                  value={num(summary?.baseAtivaFim)}
                  hint={`no fim de ${monthLabel(to)}`}
                />
                <Card
                  label="Crescimento da base"
                  value={signed(summary?.crescimentoBase)}
                  valueClass={deltaClass(summary?.crescimentoBase)}
                  hint={`abertura ${summary?.baseAtivaInicio || 0} → ${
                    summary?.baseAtivaFim || 0
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Bloco 2 — retenção da safra */}
          <div className="acq_block">
            <div className="div_home_custom">
              <BlockHead
                icon={clients}
                title="Churn"
                subtitle={`Das empresas que ENTRARAM entre ${monthLabel(
                  from,
                )} e ${monthLabel(to)}, quantas já saíram`}
              />
              <div className="acq_cards">
                <Card
                  label="Entraram"
                  value={num(retencao?.novas)}
                  hint="safra do período"
                />
                <Card
                  label="Já desativadas"
                  value={num(retencao?.desativadas)}
                  hint="da própria safra"
                />
                <Card
                  label="Perda da safra"
                  value={percent(retencao?.pctPerda)}
                  hint={`retenção de ${percent(retencao?.pctRetencao)}`}
                />
                <Card
                  label="Ativas hoje"
                  value={num(retencao?.ativasHoje)}
                  hint="da safra do período"
                />
                <Card
                  label="Entrou e saiu no período"
                  value={num(retencao?.saiuNoPeriodo)}
                  hint="cadastro e saída na mesma janela"
                />
                <Card
                  label="Tempo de vida médio"
                  value={days(retencao?.vidaMedia)}
                  hint="cadastro → desativação"
                />
              </div>
              <div className="acq_note">
                A conta fecha na safra: empresa que entrou em outro período e
                saiu dentro desta janela <strong>não</strong> é contada aqui. A
                variação da base inteira está em "Crescimento da base", acima.
              </div>
            </div>
          </div>

          {/* Gráficos */}
          <div className="acq_charts acq_block">
            <div className="div_home_custom">
              <BlockHead
                icon={dashboard}
                title="Entradas e saídas por mês"
                subtitle="Empresas que entraram no mês e, dessas, quantas já saíram"
              />
              {series.length ? (
                <ChartFrame printMode={printMode}>
                  <BarChart data={series} barGap={2}>
                    <CartesianGrid vertical={false} stroke={COR_GRADE} />
                    <XAxis
                      dataKey="label"
                      interval={0}
                      tickLine={false}
                      stroke={COR_GRADE}
                      tick={eixoTick}
                    />
                    <YAxis
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={false}
                      tick={eixoTick}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="novas"
                      name="Entraram"
                      fill={COR_ENTRADA}
                      maxBarSize={24}
                      radius={[4, 4, 0, 0]}
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="desativadas"
                      name="Já saíram"
                      fill={COR_SAIDA}
                      maxBarSize={24}
                      radius={[4, 4, 0, 0]}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ChartFrame>
              ) : (
                <div className="acq_empty">Sem dados no período.</div>
              )}
            </div>

            <div className="div_home_custom">
              <BlockHead
                icon={dashboard}
                title="Base ativa total"
                subtitle="Empresas ativas no fim de cada mês, somando todas as safras"
              />
              {series.length ? (
                <ChartFrame printMode={printMode}>
                  <LineChart data={series}>
                    <CartesianGrid vertical={false} stroke={COR_GRADE} />
                    <XAxis
                      dataKey="label"
                      interval={0}
                      tickLine={false}
                      stroke={COR_GRADE}
                      tick={eixoTick}
                    />
                    <YAxis
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={false}
                      tick={eixoTick}
                    />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="baseAtiva"
                      name="Base ativa"
                      stroke={COR_ENTRADA}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      dot={{ r: 4, strokeWidth: 0, fill: COR_ENTRADA }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ChartFrame>
              ) : (
                <div className="acq_empty">Sem dados no período.</div>
              )}
            </div>
          </div>

          {/* Bloco 3 — coorte */}
          <div className="acq_block">
            <div className="div_home_custom">
              <BlockHead
                icon={clients}
                title="Coorte por mês de entrada"
                subtitle="Quanto de cada safra de clientes segue ativa hoje"
              />
              <div className="acq_table_wrapper">
                <table className="acq_table">
                  <thead>
                    <tr>
                      <th>Mês de entrada</th>
                      <th>Entradas</th>
                      <th>Ativas hoje</th>
                      <th>% sobrevivência</th>
                      <th>Desativadas</th>
                      <th>Vida mediana</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cohorts.length ? (
                      cohorts.map((row) => (
                        <tr key={row.id}>
                          <td>{row.label}</td>
                          <td>{row.entradas}</td>
                          <td>{row.ativasHoje}</td>
                          <td>{percent(row.pctSobrevivencia)}</td>
                          <td>{row.desativadas}</td>
                          <td>{days(row.vidaMediana)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="acq_empty">
                          Sem dados no período.
                        </td>
                      </tr>
                    )}
                    {cohorts.length ? (
                      <tr className="acq_table_total">
                        <td>Total</td>
                        <td>{cohortTotals.entradas}</td>
                        <td>{cohortTotals.ativasHoje}</td>
                        <td>
                          {percent(
                            cohortTotals.entradas
                              ? Math.round(
                                  (cohortTotals.ativasHoje /
                                    cohortTotals.entradas) *
                                    1000,
                                ) / 10
                              : 0,
                          )}
                        </td>
                        <td>{cohortTotals.desativadas}</td>
                        <td>{days(retencao?.vidaMediana)}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <div className="acq_note">
                A data de desativação é o <strong>updatedAt</strong> das
                empresas inativas — o cadastro não tem campo próprio para isso.
                Se uma empresa já desativada for editada depois (logo, config),
                a saída aparece na data da edição, não na data real. A base
                ativa histórica também assume que empresa desativada não foi
                reativada.
              </div>
            </div>
          </div>
          <div className="acq_print_only acq_print_footer">
            Painel Mooda · Relatório gerado em{" "}
            {new Date().toLocaleString("pt-BR")} · A data de desativação usa o
            updatedAt do cadastro; a contabilidade de saídas é fechada na safra
            do período.
          </div>
        </>
      )}
    </div>
  );
}
